import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  Collapse,
  List,
  ListItem,
  Avatar,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Chat,
  Send,
  Close,
  SmartToy,
  Person
} from '@mui/icons-material';

const ChatBot = ({ analysisResults, isVisible = true, getAuthHeaders, userEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message when chat opens
      setMessages([{
        id: 1,
        type: 'bot',
        content: `Hi! I can help explain your SSAT analysis results. Ask me about your server performance, recommendations, or migration costs.`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      // Call the chat API
      const authHeaders = getAuthHeaders ? getAuthHeaders() : {};
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          message: currentInput,
          analysisResults: analysisResults,
          userEmail: userEmail
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        content: data.response,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      // Fallback to local response
      const fallbackResponse = generateBotResponse(currentInput, analysisResults);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        content: fallbackResponse,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateBotResponse = (question, results) => {
    if (!results) {
      return "I don't have any analysis results to discuss yet. Please upload and analyze an SSAT file first.";
    }

    const q = question.toLowerCase();
    
    // CPU related questions
    if (q.includes('cpu') || q.includes('processor')) {
      const cpu = results.analysis?.cpu;
      if (cpu) {
        return `Your server's CPU usage shows: Maximum ${cpu.max?.toFixed(1)}%, Average ${cpu.avg?.toFixed(1)}%, and 95th percentile ${cpu.p95?.toFixed(1)}%. ${cpu.max > 80 ? 'This indicates high CPU utilization that may benefit from scaling up.' : 'CPU usage appears to be within normal ranges.'}`;
      }
    }

    // Memory related questions
    if (q.includes('memory') || q.includes('ram')) {
      const memory = results.analysis?.memory;
      const server = results.analysis?.currentServer;
      if (memory && server) {
        return `Your server has ${server.memoryGB}GB total memory (${server.memoryMB}MB). Current usage: Maximum ${memory.max?.toFixed(1)}%, Average ${memory.avg?.toFixed(1)}%. ${memory.max > 85 ? 'High memory usage detected - consider memory-optimized instances.' : 'Memory usage is within acceptable ranges.'}`;
      }
    }

    // Recommendation questions
    if (q.includes('recommend') || q.includes('suggest') || q.includes('instance')) {
      const recs = results.recommendations;
      if (recs && recs.length > 0) {
        const topRec = recs[0];
        return `I recommend ${topRec.instanceType} (${topRec.serviceLabel}) ${topRec.monthlyPrice == null ? '(price unavailable)' : `at $${topRec.monthlyPrice}/month`}. This choice is based on your server's ${results.analysis?.currentServer?.cores} cores and ${results.analysis?.currentServer?.memoryGB}GB memory, with performance patterns showing ${results.analysis?.cpu?.max?.toFixed(1)}% peak CPU and ${results.analysis?.memory?.max?.toFixed(1)}% peak memory usage.`;
      }
    }

    // Cost questions
    if (q.includes('cost') || q.includes('price') || q.includes('expensive')) {
      const recs = results.recommendations;
      if (recs && recs.length > 0) {
        const costs = recs.map(r => `${r.instanceType}: ${r.monthlyPrice == null ? 'price unavailable' : `$${r.monthlyPrice}/mo`}`).join(', ');
        return `Here are your monthly cost options: ${costs}. The recommendations are based on your current workload requirements and performance patterns.`;
      }
    }

    // Server specs questions
    if (q.includes('server') || q.includes('current') || q.includes('spec')) {
      const server = results.analysis?.currentServer;
      if (server) {
        return `Your current server has ${server.cores} CPU cores and ${server.memoryGB}GB memory (${server.memoryMB}MB). Memory per core ratio is ${Math.round(server.memoryGB / parseInt(server.cores))}GB per core.`;
      }
    }

    // Default response
    return "I can help you understand your SSAT analysis. Try asking about CPU usage, memory utilization, recommendations, costs, or server specifications.";
  };

  const quickQuestions = [
    "What's my CPU usage?",
    "Explain my recommendations",
    "What are the costs?",
    "Show server specs"
  ];

  if (!isVisible || !analysisResults) {
    return null;
  }

  return (
    <>
      {/* Chat Fab Button */}
      {!isOpen && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 9999
          }}
          onClick={() => setIsOpen(true)}
        >
          <Chat />
        </Fab>
      )}

      {/* Chat Panel */}
      <Collapse in={isOpen}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 400,
            height: 500,
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SmartToy sx={{ mr: 1 }} />
              <Typography variant="h6">SSAT Assistant</Typography>
            </Box>
            <IconButton
              size="small"
              sx={{ color: 'white' }}
              onClick={() => setIsOpen(false)}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            <List dense>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                    mb: 1
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      maxWidth: '80%',
                      flexDirection: message.type === 'user' ? 'row-reverse' : 'row'
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mx: 1,
                        bgcolor: message.type === 'user' ? 'primary.main' : 'secondary.main'
                      }}
                    >
                      {message.type === 'user' ? <Person /> : <SmartToy />}
                    </Avatar>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 1.5,
                        bgcolor: message.type === 'user' ? 'primary.light' : 'grey.100',
                        color: message.type === 'user' ? 'white' : 'text.primary'
                      }}
                    >
                      <Typography variant="body2">{message.content}</Typography>
                    </Paper>
                  </Box>
                </ListItem>
              ))}
              {isLoading && (
                <ListItem sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'secondary.main' }}>
                      <SmartToy />
                    </Avatar>
                    <CircularProgress size={20} />
                  </Box>
                </ListItem>
              )}
            </List>
            <div ref={messagesEndRef} />
          </Box>

          {/* Quick Questions */}
          {messages.length <= 1 && (
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Quick questions:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {quickQuestions.map((question, index) => (
                  <Chip
                    key={index}
                    label={question}
                    size="small"
                    onClick={() => setInputValue(question)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Input */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Ask about your analysis..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isLoading}
              />
              <IconButton
                color="primary"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
              >
                <Send />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </>
  );
};

export default ChatBot;
